import {
  ColDef,
  GetQuickFilterTextParams,
  IRowNode,
  KeyCreatorParams,
  ValueFormatterParams,
  ValueGetterParams,
} from 'ag-grid-community';
import { t } from 'i18next';

import { IExtractorVendorMapping } from 'companySettings/types';
import { formatDate } from 'core/utils';

import { ActionsCell, VendorCell, VendorContactCell, VendorLocationCell } from './cells';

interface IColumnDefParams {
  canEdit?: boolean;
  canRemove?: boolean;
}

type MappingNode = IRowNode<IExtractorVendorMapping>;
type MappingParams = ValueGetterParams<IExtractorVendorMapping>;
type MappingQuickFilterParams = GetQuickFilterTextParams<IExtractorVendorMapping>;
type MappingKeyParams = KeyCreatorParams<IExtractorVendorMapping, string>;
type MappingFormatterParams = ValueFormatterParams<IExtractorVendorMapping, string>;

const getSearchText = (...values: (string | null | undefined)[]) =>
  values.filter(Boolean).join(' ');

const compareNames = (nameA?: string, nameB?: string) => (nameA ?? '').localeCompare(nameB ?? '');

// Vendor Location and Vendor Contact show two lines and each behaviour reads a different one, so
// all five hooks are set explicitly. `valueGetter` is what the clipboard takes (neither grid
// provider sets `processCellForClipboard` and neither column has a `valueFormatter`, so a copy is
// the raw value), `comparator` sorts on the name on the first line, `filterValueGetter` /
// `getQuickFilterText` search both lines, and `keyCreator` groups by the name. Every getter guards
// `data`: a row-grouped grid has group rows with none.
// Vendor and Updated By hold a single value, so their `valueGetter` alone serves the copy, the
// group key, the filter and both searches.
// Only the actions column suppresses its header menu button — that button is the sole filter entry
// point once the provider hides the floating filter row on a list of three rows or fewer.
export const getColumnDef = ({
  canEdit = false,
  canRemove = false,
}: IColumnDefParams = {}): ColDef[] => [
  ...(canEdit || canRemove
    ? [
        {
          colId: 'actions',
          headerName: '',
          width: 90,
          maxWidth: 90,
          sortable: false,
          resizable: false,
          filter: false,
          enableRowGroup: false,
          suppressMovable: true,
          suppressHeaderMenuButton: true,
          suppressColumnsToolPanel: true,
          pinned: 'left',
          lockPinned: true,
          cellClass: 'noDecorationCell',
          cellRenderer: ActionsCell,
          cellRendererParams: { canEdit, canRemove },
        } as ColDef,
      ]
    : []),
  {
    colId: 'extractorId',
    headerName: t('headerExtractorId'),
    field: 'extractorId',
    filter: 'agTextColumnFilter',
    // Every extractor id is unique, so grouping by it would only ever produce one row per group.
    enableRowGroup: false,
    flex: 1,
  },
  {
    // The parent vendor alone: one vendor owns many locations (35 for Accu-Tech), so the AP number
    // that identifies a single one belongs to the Vendor Location column beside this one.
    colId: 'vendor',
    headerName: t('headerVendor'),
    filter: 'agTextColumnFilter',
    flex: 1.25,
    cellRenderer: VendorCell,
    valueGetter: ({ data }: MappingParams) => data && (data.vendor?.name ?? ''),
    // The only hook the value getter does not already cover: AG Grid's default sorts strings by
    // code point, which would order a lower-case vendor name after every upper-case one.
    comparator: (_, __, nodeA: MappingNode, nodeB: MappingNode) =>
      compareNames(nodeA?.data?.vendor?.name, nodeB?.data?.vendor?.name),
  },
  {
    colId: 'vendorLocation',
    headerName: t('headerVendorLocation'),
    filter: 'agTextColumnFilter',
    flex: 1.25,
    cellRenderer: VendorLocationCell,
    // The AP number is the location's identifier, so it is what a right-click copy yields.
    valueGetter: ({ data }: MappingParams) => data && (data.vendor?.accountsPayableId ?? ''),
    // Group By must group on the name the cell leads with, not on the AP number the value holds.
    // AG Grid reads `keyCreator` only for row grouping/pivoting and the Set filter — this column
    // filters as text — so the copy, sort, filter and quick-search behaviours below are untouched.
    keyCreator: ({ data }: MappingKeyParams) => data?.vendor?.vendorLocationName ?? '',
    comparator: (_, __, nodeA: MappingNode, nodeB: MappingNode) =>
      compareNames(
        nodeA?.data?.vendor?.vendorLocationName,
        nodeB?.data?.vendor?.vendorLocationName
      ),
    filterValueGetter: ({ data }: MappingParams) =>
      data && getSearchText(data.vendor?.vendorLocationName, data.vendor?.accountsPayableId),
    getQuickFilterText: ({ data }: MappingQuickFilterParams) =>
      getSearchText(data?.vendor?.vendorLocationName, data?.vendor?.accountsPayableId),
  },
  {
    colId: 'vendorContact',
    headerName: t('headerVendorContact'),
    filter: 'agTextColumnFilter',
    flex: 1.25,
    cellRenderer: VendorContactCell,
    valueGetter: ({ data }: MappingParams) => data && (data.vendorContact?.email ?? ''),
    keyCreator: ({ data }: MappingKeyParams) => data?.vendorContact?.name ?? '',
    comparator: (_, __, nodeA: MappingNode, nodeB: MappingNode) =>
      compareNames(nodeA?.data?.vendorContact?.name, nodeB?.data?.vendorContact?.name),
    filterValueGetter: ({ data }: MappingParams) =>
      data && getSearchText(data.vendorContact?.name, data.vendorContact?.email),
    getQuickFilterText: ({ data }: MappingQuickFilterParams) =>
      getSearchText(data?.vendorContact?.name, data?.vendorContact?.email),
  },
  {
    colId: 'updatedAt',
    headerName: t('headerUpdatedAt'),
    field: 'updatedAt',
    // The date filter needs no `filterParams`: the provider injects its own `comparator` (the raw
    // `updated_at` string is not a Date) and the supported filter options for every date column.
    filter: 'agDateColumnFilter',
    // The list arrives `updated_at desc` and the grid must show it that way; a user's own sort
    // replaces this on the first header click.
    sort: 'desc',
    // A second-granular timestamp groups into one row per mapping.
    enableRowGroup: false,
    width: 170,
    // A group row has no timestamp, and `formatDate` answers an empty one with the `-`
    // placeholder — the only column that would print a dash on a grouped row, since the default
    // renderer every other column uses already blanks a group row itself.
    valueFormatter: ({ node, value }: MappingFormatterParams) =>
      node?.group ? '' : formatDate(value, true),
  },
  {
    // One field, so the value getter serves the group key, the copy, the sort and both searches.
    colId: 'updatedBy',
    headerName: t('headerUpdatedBy'),
    filter: 'agTextColumnFilter',
    width: 170,
    valueGetter: ({ data }: MappingParams) => data && (data.updatedBy?.name ?? ''),
  },
];
